import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@apollo/client';
import toast from 'react-hot-toast';
import { XMarkIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { CREATE_ROLE, UPDATE_ROLE, GET_ALL_ROLES } from '../../graphql/queries';
import { Spinner } from '../common/Spinner';

/**
 * Role to seed the modal in edit mode. Shape matches the Role GraphQL type
 * the parent page already fetches via GET_ALL_ROLES.
 */
export interface EditableRole {
  roleId: string;
  roleName: string;
  description?: string | null;
  permissions: string[];
  isSystemRole?: boolean;
}

export interface AssignRoleModalProps {
  onClose: () => void;
  onChanged: () => void;
  /** When provided, the modal is in "edit" mode for the given role. */
  role?: EditableRole | null;
}

/**
 * Full permission catalog, grouped so the UI can render tidy sections.
 * Source of truth for the wording is `backend/src/constants/permissions.ts`
 * — keep in sync when new permissions are introduced.
 */
const PERMISSION_GROUPS: Array<{
  labelKey: string;
  defaultLabel: string;
  permissions: string[];
}> = [
  {
    labelKey: 'admin:userRoles.modal.groups.campaigns',
    defaultLabel: 'Campaigns',
    permissions: [
      'campaigns:create',
      'campaigns:read',
      'campaigns:update',
      'campaigns:delete',
      'campaigns:publish',
    ],
  },
  {
    labelKey: 'admin:userRoles.modal.groups.matches',
    defaultLabel: 'Matches',
    permissions: [
      'matches:create',
      'matches:read',
      'matches:shortlist',
      'matches:request',
      'matches:accept',
      'matches:reject',
      'matches:cancel',
      'matches:complete',
    ],
  },
  {
    labelKey: 'admin:userRoles.modal.groups.users',
    defaultLabel: 'Users',
    permissions: ['users:read', 'users:update', 'users:delete', 'users:impersonate'],
  },
  {
    labelKey: 'admin:userRoles.modal.groups.messages',
    defaultLabel: 'Messages',
    permissions: ['messages:send', 'messages:read', 'messages:delete'],
  },
  {
    labelKey: 'admin:userRoles.modal.groups.points',
    defaultLabel: 'Points',
    permissions: [
      'points:read',
      'points:allocate',
      'points:deduct',
      'points:refund',
      'points:configure',
    ],
  },
  {
    labelKey: 'admin:userRoles.modal.groups.roles',
    defaultLabel: 'Roles',
    permissions: [
      'roles:create',
      'roles:read',
      'roles:update',
      'roles:delete',
      'roles:associate',
    ],
  },
  {
    labelKey: 'admin:userRoles.modal.groups.system',
    defaultLabel: 'System',
    permissions: ['system:admin', 'system:moderate', 'system:audit'],
  },
];

/**
 * Presets mirroring the built-in system roles so operators can bootstrap a
 * new role quickly, then tweak the permission list before saving.
 */
const ROLE_PRESETS: Array<{
  key: string;
  nameSuggestion: string;
  description: string;
  permissions: string[];
}> = [
  {
    key: 'admin',
    nameSuggestion: 'admin-custom',
    description: 'Full administrative access',
    permissions: ['*:*'],
  },
  {
    key: 'brand',
    nameSuggestion: 'brand-custom',
    description: 'Brand / host-level access for campaign management',
    permissions: [
      'campaigns:create',
      'campaigns:read',
      'campaigns:update',
      'campaigns:delete',
      'campaigns:publish',
      'matches:read',
      'matches:shortlist',
      'matches:request',
      'matches:cancel',
      'matches:complete',
      'messages:send',
      'messages:read',
      'points:read',
      'users:read',
      'users:update',
    ],
  },
  {
    key: 'influencer',
    nameSuggestion: 'influencer-custom',
    description: 'Influencer-level access for discovery and applications',
    permissions: [
      'campaigns:read',
      'matches:create',
      'matches:read',
      'matches:accept',
      'matches:reject',
      'messages:send',
      'messages:read',
      'points:read',
      'users:read',
      'users:update',
    ],
  },
];

const ROLE_NAME_REGEX = /^[a-z][a-z0-9_-]{1,30}$/;

/**
 * 권한 추가 / 권한 수정 Modal.
 *
 * Dual-purpose: when `role` is omitted the modal creates a new role via
 * CREATE_ROLE; when `role` is supplied it edits description + permissions
 * via UPDATE_ROLE. The role name itself is immutable on edit because
 * changing it would break existing role associations.
 */
const AssignRoleModal: React.FC<AssignRoleModalProps> = ({ onClose, onChanged, role }) => {
  const { t } = useTranslation(['admin', 'common']);

  const isEdit = !!role;

  const [roleName, setRoleName] = useState(role?.roleName ?? '');
  const [description, setDescription] = useState(role?.description ?? '');
  const [permissions, setPermissions] = useState<string[]>(
    (role?.permissions ?? []).filter((p) => p !== '*:*'),
  );
  const [wildcardAll, setWildcardAll] = useState(
    (role?.permissions ?? []).includes('*:*'),
  );
  const [activePreset, setActivePreset] = useState<string>('');

  // Pull existing roles so we can prevent duplicate names client-side.
  const { data: existingRolesData } = useQuery<{ getAllRoles: { roles: Array<{ roleId: string; roleName: string }> } }>(
    GET_ALL_ROLES,
    { variables: { includeInactive: true } },
  );
  const existingNames = useMemo(() => {
    const set = new Set<string>();
    for (const r of existingRolesData?.getAllRoles?.roles ?? []) {
      // Exclude the role being edited from the duplicate check.
      if (role && r.roleId === role.roleId) continue;
      set.add(r.roleName.toLowerCase());
    }
    return set;
  }, [existingRolesData, role]);

  const [createRole, { loading: creating }] = useMutation(CREATE_ROLE);
  const [updateRole, { loading: updating }] = useMutation(UPDATE_ROLE);
  const saving = creating || updating;

  // Reset form when the role prop changes (e.g. user clicks Edit on a different row).
  useEffect(() => {
    setRoleName(role?.roleName ?? '');
    setDescription(role?.description ?? '');
    setPermissions((role?.permissions ?? []).filter((p) => p !== '*:*'));
    setWildcardAll((role?.permissions ?? []).includes('*:*'));
    setActivePreset('');
  }, [role]);

  const togglePermission = (p: string) => {
    setActivePreset('');
    setPermissions((prev) => (prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p]));
  };

  const applyPreset = (presetKey: string) => {
    const p = ROLE_PRESETS.find((r) => r.key === presetKey);
    if (!p) return;
    setActivePreset(presetKey);
    if (p.permissions.includes('*:*')) {
      setWildcardAll(true);
      setPermissions([]);
    } else {
      setWildcardAll(false);
      setPermissions(p.permissions);
    }
    if (!isEdit) {
      if (!roleName) setRoleName(p.nameSuggestion);
      if (!description) setDescription(p.description);
    }
  };

  const nameIsValid = isEdit ? true : ROLE_NAME_REGEX.test(roleName);
  const nameIsTaken = isEdit ? false : existingNames.has(roleName.toLowerCase());
  const hasPermissions = wildcardAll || permissions.length > 0;
  const canSubmit = nameIsValid && !nameIsTaken && hasPermissions && !saving;

  const handleSubmit = async () => {
    if (!canSubmit) return;

    const finalPermissions = wildcardAll ? ['*:*'] : permissions;

    try {
      if (isEdit && role) {
        await updateRole({
          variables: {
            input: {
              roleId: role.roleId,
              description: description || `Custom role: ${role.roleName}`,
              permissions: finalPermissions,
            },
          },
        });
        toast.success(
          t('admin:userRoles.modal.updateSuccess', '{{role}} 권한이 수정되었습니다', {
            role: role.roleName,
          }),
        );
      } else {
        await createRole({
          variables: {
            input: {
              roleName,
              description: description || `Custom role: ${roleName}`,
              permissions: finalPermissions,
            },
          },
        });
        toast.success(
          t('admin:userRoles.modal.createSuccess', '{{role}} 권한이 생성되었습니다', {
            role: roleName,
          }),
        );
      }
      onChanged();
      onClose();
    } catch (err: any) {
      toast.error(
        err?.message ||
          (isEdit
            ? t('admin:userRoles.modal.updateFailed', '권한 수정 실패')
            : t('admin:userRoles.modal.createFailed', '권한 생성 실패')),
      );
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-bold text-gray-900">
            {isEdit
              ? t('admin:userRoles.modal.editTitle', '권한 수정')
              : t('admin:userRoles.modal.title', '권한 추가')}
          </h2>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-5">
          {/* Role presets — only on create */}
          {!isEdit && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('admin:userRoles.modal.presetLabel', '역할 템플릿')}
              </label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                {ROLE_PRESETS.map((p) => {
                  const active = activePreset === p.key;
                  return (
                    <button
                      key={p.key}
                      type="button"
                      onClick={() => applyPreset(p.key)}
                      className={`text-left p-3 border rounded-lg transition-colors ${
                        active
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center gap-1.5">
                        {active && <CheckCircleIcon className="w-4 h-4 text-blue-600" />}
                        <span className="text-sm font-semibold text-gray-900">
                          {t(`admin:userRoles.modal.presets.${p.key}`, p.key)}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">{p.description}</p>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Role name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('admin:userRoles.modal.roleName', '역할 이름')}{' '}
              {!isEdit && <span className="text-red-500">*</span>}
            </label>
            <input
              type="text"
              value={roleName}
              onChange={(e) => setRoleName(e.target.value.toLowerCase())}
              disabled={isEdit}
              placeholder="e.g. admin-readonly"
              className={`w-full px-3 py-2 text-sm border rounded-lg focus:ring-1 focus:outline-none ${
                roleName && (!nameIsValid || nameIsTaken)
                  ? 'border-red-300 focus:ring-red-500'
                  : 'border-gray-300 focus:ring-blue-500'
              } ${isEdit ? 'bg-gray-50 cursor-not-allowed' : ''}`}
            />
            {!isEdit && roleName && !nameIsValid && (
              <p className="mt-1 text-xs text-red-600">
                {t(
                  'admin:userRoles.modal.roleNameInvalid',
                  '영문 소문자로 시작하고 영문/숫자/-/_만 사용 (2~31자)',
                )}
              </p>
            )}
            {nameIsTaken && (
              <p className="mt-1 text-xs text-red-600">
                {t('admin:userRoles.modal.roleNameTaken', '이미 존재하는 역할 이름입니다')}
              </p>
            )}
            {isEdit && (
              <p className="mt-1 text-xs text-gray-500">
                {t('admin:userRoles.modal.roleNameLocked', '역할 이름은 변경할 수 없습니다.')}
              </p>
            )}
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('admin:userRoles.modal.description', '설명')}
            </label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t(
                'admin:userRoles.modal.descriptionPlaceholder',
                '역할 설명 (선택)',
              ) as string}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:outline-none focus:ring-blue-500"
            />
          </div>

          {/* Full admin shortcut */}
          <div className="p-3 border border-red-100 bg-red-50/50 rounded-lg">
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={wildcardAll}
                onChange={(e) => {
                  setWildcardAll(e.target.checked);
                  if (e.target.checked) setPermissions([]);
                  setActivePreset('');
                }}
                className="mt-0.5 w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
              />
              <div>
                <div className="text-sm font-medium text-red-800">
                  {t('admin:userRoles.modal.wildcardAllTitle', '전체 권한 (*:*)')}
                </div>
                <p className="text-xs text-red-700/80 mt-0.5">
                  {t(
                    'admin:userRoles.modal.wildcardAllDesc',
                    '이 역할은 모든 리소스에 대한 모든 작업을 허용합니다. Admin 용으로만 사용하세요.',
                  )}
                </p>
              </div>
            </label>
          </div>

          {/* Permission groups */}
          <div className={wildcardAll ? 'opacity-40 pointer-events-none' : ''}>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('admin:userRoles.modal.permissions', '권한 선택')}
              <span className="ml-1 text-xs text-gray-500">
                ({permissions.length} {t('admin:userRoles.modal.selected', '선택됨')})
              </span>
            </label>

            <div className="space-y-3 max-h-80 overflow-y-auto pr-1">
              {PERMISSION_GROUPS.map((group) => {
                const allChecked = group.permissions.every((p) => permissions.includes(p));
                const someChecked = group.permissions.some((p) => permissions.includes(p));
                return (
                  <div key={group.labelKey} className="border border-gray-200 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-semibold text-gray-700 uppercase tracking-wider">
                        {t(group.labelKey, group.defaultLabel)}
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          setActivePreset('');
                          if (allChecked) {
                            setPermissions((prev) =>
                              prev.filter((p) => !group.permissions.includes(p)),
                            );
                          } else {
                            setPermissions((prev) => [
                              ...prev,
                              ...group.permissions.filter((p) => !prev.includes(p)),
                            ]);
                          }
                        }}
                        className="text-xs text-blue-600 hover:text-blue-800"
                      >
                        {allChecked
                          ? t('admin:userRoles.modal.deselectAll', '전체 해제')
                          : someChecked
                            ? t('admin:userRoles.modal.selectRemaining', '나머지 선택')
                            : t('admin:userRoles.modal.selectAll', '전체 선택')}
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-1.5">
                      {group.permissions.map((p) => {
                        const checked = permissions.includes(p);
                        return (
                          <label
                            key={p}
                            className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => togglePermission(p)}
                              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                            />
                            <code className="text-xs text-gray-700 font-mono">{p}</code>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>

            {!wildcardAll && !hasPermissions && (
              <p className="mt-2 text-xs text-red-600">
                {t('admin:userRoles.modal.needOnePermission', '권한을 1개 이상 선택하세요.')}
              </p>
            )}
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            {t('common:cancel', '취소')}
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {saving && <Spinner size="small" />}
            {isEdit
              ? t('admin:userRoles.modal.saveBtn', '저장')
              : t('admin:userRoles.modal.createBtn', '역할 생성')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssignRoleModal;
